/**
 * Search inside a Help document.
 *
 * The user guide is one long markdown file. These helpers cut it at its
 * headings so the Help tab can show only the sections that mention the words
 * typed, and wrap each occurrence of those words in a <mark> when rendering.
 * Everything here is pure; HelpTab owns the state.
 */
import type { Element, ElementContent, Root, Text } from 'hast';

export interface GuideSection {
  /** Position in the document; the full-guide anchor is keyed on it. */
  index: number;
  /** 0 for the preamble before the first `##`; otherwise the heading level. */
  level: 0 | 2 | 3;
  /** Heading text with the `#` marks and inline markdown removed; '' for the preamble. */
  heading: string;
  /** The enclosing `##` heading of a `###` section; '' otherwise. */
  parent: string;
  /** Index of the enclosing `##` section for a `###` section; null otherwise. */
  parentIndex: number | null;
  /** The section's lines, heading included, exactly as in the source. */
  markdown: string;
  /** What a query is matched against: the markdown lowercased, link targets removed. */
  text: string;
}

const HEADING = /^(#{2,3})\s+(.*?)\s*#*\s*$/;
const FENCE = /^(```|~~~)/;

/** Inline markdown stripped from a heading line so it reads as plain text. */
function plainHeading(raw: string): string {
  return raw
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`]/g, '')
    .trim();
}

/** The markdown as search text: lowercased, with link and image targets removed
 *  so a word that only appears inside a URL cannot match. */
function searchableText(markdown: string): string {
  return markdown.replace(/\]\([^)]*\)/g, ']').toLowerCase();
}

/**
 * Cuts a markdown document at every `##` and `###` heading. Deeper headings
 * stay inside their section, a `###` before the first `##` (a subtitle) stays
 * in the preamble, and everything before the first `##` is one preamble
 * section at level 0. Joining every section's `markdown` with '\n' gives the
 * document back unchanged.
 */
export function splitGuide(markdown: string): GuideSection[] {
  const sections: GuideSection[] = [];
  let lines: string[] = [];
  let level: 0 | 2 | 3 = 0;
  let heading = '';
  let parent = '';
  let parentIndex: number | null = null;
  let lastH2 = '';
  let lastH2Index: number | null = null;
  let inFence = false;

  const flush = () => {
    if (lines.length === 0) return;
    const md = lines.join('\n');
    sections.push({
      index: sections.length,
      level,
      heading,
      parent,
      parentIndex,
      markdown: md,
      text: searchableText(md),
    });
    lines = [];
  };

  for (const line of markdown.split('\n')) {
    if (FENCE.test(line)) inFence = !inFence;
    const m = inFence ? null : HEADING.exec(line);
    if (m && (m[1].length === 2 || lastH2Index !== null)) {
      flush();
      level = m[1].length === 2 ? 2 : 3;
      heading = plainHeading(m[2]);
      if (level === 2) {
        lastH2 = heading;
        lastH2Index = sections.length;
        parent = '';
        parentIndex = null;
      } else {
        parent = lastH2;
        parentIndex = lastH2Index;
      }
    }
    lines.push(line);
  }
  flush();
  return sections;
}

/** One `##` section of the outline with the `###` sections it contains. */
export interface OutlineEntry {
  section: GuideSection;
  children: GuideSection[];
}

/** The document's `##` sections in order, each with its `###` sections. A
 *  `###` with no enclosing `##` becomes an entry of its own. */
export function buildOutline(sections: GuideSection[]): OutlineEntry[] {
  const entries: OutlineEntry[] = [];
  for (const s of sections) {
    if (s.level === 2) {
      entries.push({ section: s, children: [] });
    } else if (s.level === 3) {
      const owner = entries.find((e) => e.section.index === s.parentIndex);
      if (owner) owner.children.push(s);
      else entries.push({ section: s, children: [] });
    }
  }
  return entries;
}

/** "Section 7: Existing Load Tab" as a number and a title for a compact list;
 *  a heading without the prefix keeps its whole text as the title. */
export function outlineLabel(heading: string): { number: string; title: string } {
  const m = /^Section\s+(\d+):\s*(.+)$/.exec(heading);
  return m ? { number: m[1], title: m[2] } : { number: '', title: heading };
}

/** Light stemming so "deleting" also finds "delete" and "deleted": a trailing
 *  ing / ies / ed / es / s is dropped when enough of the word is left to still
 *  mean something. The stem is always a substring of the original word, so
 *  recall only ever goes up. */
function stem(word: string): string {
  const rules: Array<[string, number]> = [['ing', 4], ['ies', 3], ['ed', 4], ['es', 3], ['s', 3]];
  for (const [suffix, keep] of rules) {
    if (word.endsWith(suffix) && word.length - suffix.length >= keep) {
      return word.slice(0, -suffix.length);
    }
  }
  return word;
}

/** The words of a query, lowercased and stemmed, with outer punctuation and
 *  one-letter words dropped. Empty means "no search". */
export function searchTerms(query: string): string[] {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
    .filter((w) => w.length >= 2)
    .map(stem);
  return Array.from(new Set(words));
}

/** True when every term appears somewhere in the section. */
export function sectionMatches(section: GuideSection, terms: string[]): boolean {
  return terms.every((t) => section.text.includes(t));
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Where the terms occur in a run of text, as [start, end) offsets. Each hit is
 * widened to the whole word around it so "delet" highlights "Deleting", and
 * overlapping hits are merged. Case-insensitive.
 */
export function matchRanges(text: string, terms: string[]): Array<[number, number]> {
  const hits: Array<[number, number]> = [];
  for (const term of terms) {
    if (!term) continue;
    for (const m of text.matchAll(new RegExp(escapeRegExp(term), 'gi'))) {
      let start = m.index;
      let end = start + m[0].length;
      while (start > 0 && WORD_CHAR.test(text[start - 1])) start--;
      while (end < text.length && WORD_CHAR.test(text[end])) end++;
      hits.push([start, end]);
    }
  }
  hits.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of hits) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return merged;
}

function splitText(node: Text, terms: string[]): ElementContent[] {
  const ranges = matchRanges(node.value, terms);
  if (ranges.length === 0) return [node];
  const out: ElementContent[] = [];
  let pos = 0;
  for (const [s, e] of ranges) {
    if (s > pos) out.push({ type: 'text', value: node.value.slice(pos, s) });
    out.push({
      type: 'element',
      tagName: 'mark',
      properties: {},
      children: [{ type: 'text', value: node.value.slice(s, e) }],
    });
    pos = e;
  }
  if (pos < node.value.length) out.push({ type: 'text', value: node.value.slice(pos) });
  return out;
}

function highlight(node: Root | Element, terms: string[]): void {
  const next: ElementContent[] = [];
  for (const child of node.children) {
    if (child.type === 'doctype') continue; // markdown never produces one
    if (child.type === 'text') {
      next.push(...splitText(child, terms));
    } else {
      if (child.type === 'element') highlight(child, terms);
      next.push(child);
    }
  }
  node.children = next;
}

/** A rehype plugin that wraps every occurrence of the terms in a <mark>. Pass
 *  it to ReactMarkdown as `rehypePlugins={[[rehypeHighlight, terms]]}`. */
export function rehypeHighlight(terms: string[]) {
  return (tree: Root): void => {
    if (terms.length > 0) highlight(tree, terms);
  };
}
